import type { AppError } from "@/types";

export type UnenrollmentErrorCode =
  // services.ErrUnenrollment*
  | "NOT_ENROLLED"
  | "OWNER_REQUIRED"
  | "QUORUM_UNREACHABLE"
  | "ALREADY_PENDING"
  | "INVALID_SIGNER"
  | "NOT_PENDING"
  | "SIGNATURE_INVALID"
  | "CANCELLATION_CONFLICT"
  // Shared multisig policy sentinels
  | "TENANT_NOT_ENROLLED"
  | "TRANSACTION_EXPIRED"
  | "TRANSACTION_NOT_FOUND"
  | "TRANSACTION_STATUS_INVALID"
  | "SIGNATURE_VERIFICATION_FAILED"
  | "POLICY_NOT_SATISFIED"
  // data.Err*
  | "ALREADY_EXISTS"
  | "NOT_FOUND"
  // Request validation (renderValidationError)
  | "MISSING_TX_ID"
  | "INVALID_SIGNER_PUBLIC_KEY"
  | "MISSING_SIGNED_XDR"
  // Role / transport
  | "FORBIDDEN"
  | "UNAUTHORIZED"
  | "UNKNOWN";

type Rule = { code: UnenrollmentErrorCode; match: string; message: string };

export const UNENROLLMENT_ERROR_RULES: ReadonlyArray<Rule> = [
  {
    code: "NOT_ENROLLED",
    match: "organization is not enrolled in multisig",
    message:
      "Your organization isn't currently using co-signing, so there's nothing to remove. Set it up in Team members.",
  },
  {
    code: "OWNER_REQUIRED",
    match: "unenrollment requires an owner",
    message: "Only Owners can remove co-signing.",
  },
  {
    code: "QUORUM_UNREACHABLE",
    match: "two-owner unenrollment quorum is unreachable",
    message:
      "Removal needs two active Owners with signing weight. One of them can't sign right now, so please contact support.",
  },
  {
    code: "ALREADY_PENDING",
    match: "an unenrollment transaction is already active",
    message:
      "A removal is already in progress. Finish that one first — only one removal can run at a time.",
  },
  {
    code: "INVALID_SIGNER",
    match: "unenrollment signature must be from an active owner",
    message: "Only the active co-signing Owners can sign this removal.",
  },
  {
    code: "NOT_PENDING",
    match: "transaction is not a pending unenrollment for this organization",
    message:
      "This removal isn't waiting for signatures anymore. Refresh the page to see where it stands.",
  },
  {
    code: "SIGNATURE_INVALID",
    match: "verifying unenrollment signature",
    message:
      "The signature doesn't match the key you chose. In VIA Wallet, sign the removal with that same key and try again.",
  },
  {
    code: "CANCELLATION_CONFLICT",
    match: "unenrollment transaction cannot be cancelled",
    message: "This removal can't be cancelled anymore. Refresh the page to see where it stands.",
  },
  {
    code: "TENANT_NOT_ENROLLED",
    match: "tenant is not enrolled in multisig",
    message: "Your organization isn't currently using co-signing.",
  },
  {
    code: "TRANSACTION_EXPIRED",
    match: "transaction has expired",
    message: "This removal expired before both Owners could sign. Start a new one.",
  },
  {
    code: "TRANSACTION_STATUS_INVALID",
    match: "transaction status is not valid for signing",
    message:
      "This removal isn't waiting for signatures anymore. Refresh the page to see where it stands.",
  },
  {
    code: "TRANSACTION_NOT_FOUND",
    match: "transaction not found for this tenant",
    message: "We couldn't find that removal. Refresh the page and try again.",
  },
  {
    code: "SIGNATURE_VERIFICATION_FAILED",
    match: "signature verification failed",
    message:
      "The signature doesn't match the key you chose. In VIA Wallet, sign the removal with that same key and try again.",
  },
  {
    code: "POLICY_NOT_SATISFIED",
    match: "policy not satisfied",
    message: "This removal doesn't meet the co-signing policy yet.",
  },
  {
    // On unenrollment this only comes from UNIQUE (transaction_id,
    // signer_public_key): the same Owner submitted a second signature. A second
    // removal is refused with ErrUnenrollmentAlreadyPending instead.
    code: "ALREADY_EXISTS",
    match: "record already exists",
    message: "Your signature is already on this removal. Refresh the page to see where it stands.",
  },
  // Request validation (renderValidationError)
  {
    code: "MISSING_TX_ID",
    match: "tx_id is required",
    message: "We lost track of the removal. Refresh the page and try again.",
  },
  {
    code: "INVALID_SIGNER_PUBLIC_KEY",
    match: "signer_public_key must be a valid stellar g-address",
    message: "That isn't a valid Stellar public key.",
  },
  {
    code: "MISSING_SIGNED_XDR",
    match: "signed_xdr is required",
    message: "Sign the removal in VIA Wallet first.",
  },
];

const STATUS_FALLBACKS: ReadonlyArray<{
  status: number;
  code: UnenrollmentErrorCode;
  message: string;
}> = [
  { status: 401, code: "UNAUTHORIZED", message: "Your session has ended. Sign in again." },
  {
    status: 403,
    code: "FORBIDDEN",
    message: "You don't have permission to remove co-signing. Only Owners do.",
  },
  { status: 404, code: "NOT_FOUND", message: "There's no removal in progress." },
];

export const UNENROLLMENT_GENERIC_ERROR_MESSAGE =
  "Something went wrong removing co-signing. Try again.";

export class UnenrollmentError extends Error {
  readonly code: UnenrollmentErrorCode;
  readonly status?: number;
  readonly rawMessage: string;

  constructor(code: UnenrollmentErrorCode, message: string, rawMessage: string, status?: number) {
    super(message);
    this.name = "UnenrollmentError";
    this.code = code;
    this.status = status;
    this.rawMessage = rawMessage;
  }
}

const backendText = (err: Partial<AppError>): string => {
  const parts = [err.message ?? ""];
  for (const value of Object.values(err.extras ?? {})) {
    if (typeof value === "string") parts.push(value);
  }
  return parts.join(" | ");
};

export const mapUnenrollmentError = (err: unknown): UnenrollmentError => {
  if (err instanceof UnenrollmentError) return err;

  const appError: Partial<AppError> =
    err && typeof err === "object" ? (err as Partial<AppError>) : { message: String(err ?? "") };
  const raw = backendText(appError);
  const haystack = raw.toLowerCase();
  const status = appError.status;

  const rule = UNENROLLMENT_ERROR_RULES.find((r) => haystack.includes(r.match));
  if (rule) return new UnenrollmentError(rule.code, rule.message, raw, status);

  const fallback = STATUS_FALLBACKS.find((f) => f.status === status);
  if (fallback) return new UnenrollmentError(fallback.code, fallback.message, raw, status);

  return new UnenrollmentError("UNKNOWN", UNENROLLMENT_GENERIC_ERROR_MESSAGE, raw, status);
};

export const getUnenrollmentErrorMessage = (err: unknown): string =>
  mapUnenrollmentError(err).message;
